import { NgModule } from '@angular/core';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreRouterConnectingModule, routerReducer } from '@ngrx/router-store';

import { environment } from '../../environments/environment';
import { reducers, metaReducers } from './index';

// Import feature state modules
import { AuthStateModule } from './auth/auth-state.module';
import { UserStateModule } from './user/user-state.module';
import { FeedbackStateModule } from './feedback/feedback-state.module';
import { UserContactStateModule } from './user-contact/user-contact-state.module';
import { StripeStateModule } from './stripe/stripe-state.module';
import { AvatarStateModule } from './avatar/avatar-state.module';
import { VideoSearchStateModule } from './video-search/video-search-state.module';
import { LanguageStateModule } from './language/language-state.module';
import { AudioStateModule } from './audio/audio-state.module';
import { UiStateModule } from './ui/ui-state.module';
import { NotificationStateModule } from './notification/notification-state.module';
import { UserPreferencesStateModule } from './user-preferences/user-preferences-state.module';
import { UserSettingsStateModule } from './user-settings/user-settings-state.module';
import { ManagerFeedbackStateModule } from './manager-feedback/manager-feedback-state.module';
import { MatchingStateModule } from './matching/matching-state.module';
import { CareLinksStateModule } from './care-links/care-links-state.module';

/**
 * Root State Module
 * 
 * This module configures the root NgRx store and registers all feature modules.
 * It should be imported ONLY ONCE in AppModule.
 * 
 * Structure:
 * - Root state (router, etc.)
 * - Feature states (auth, notifications, avatar, etc.) are registered via feature modules
 * - DevTools (development only)
 * - Router state synchronization
 * 
 * As you migrate more features to NgRx, create new feature state modules
 * and import them here.
 */
@NgModule({
  imports: [
    // Root store configuration
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictStateSerializability: false, // Disabled due to Amplify objects
        strictActionSerializability: false, // Disabled due to Amplify objects
        strictActionWithinNgZone: false, // Disabled - ngrxOnInitEffects runs outside NgZone
        strictActionTypeUniqueness: true,
      }
    }),

    // Root effects module
    EffectsModule.forRoot([]),

    // Router state synchronization
    StoreRouterConnectingModule.forRoot(),

    // DevTools - only in development
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode in production
      autoPause: true, // Pauses recording actions when extension window is not open
      trace: false, // If set to true, will include stack trace for every action
      traceLimit: 75, // Maximum stack trace frames to be stored
    }),

    // Feature state modules
    AuthStateModule,
    UserStateModule,
    FeedbackStateModule,
    UserContactStateModule,
    StripeStateModule,
    AvatarStateModule,
    VideoSearchStateModule,
    LanguageStateModule,
    AudioStateModule,
    UiStateModule,
    NotificationStateModule,
    UserPreferencesStateModule,
    UserSettingsStateModule,
    ManagerFeedbackStateModule,
    MatchingStateModule,
    CareLinksStateModule,
    
    // TODO: Add more feature state modules as they are created
    // Examples:
    // SubscriptionStateModule,
  ],
  exports: [
    StoreModule,
    EffectsModule,
    StoreDevtoolsModule,
    StoreRouterConnectingModule,
    
    // Export feature modules so they're available throughout the app
    AuthStateModule,
    UserStateModule,
    FeedbackStateModule,
    UserContactStateModule,
    StripeStateModule,
    AvatarStateModule,
    VideoSearchStateModule,
    LanguageStateModule,
    AudioStateModule,
    UiStateModule,
    NotificationStateModule,
    UserPreferencesStateModule,
    UserSettingsStateModule,
    ManagerFeedbackStateModule,
    MatchingStateModule,
    CareLinksStateModule,
  ]
})
export class StateModule {}
